// App.js
import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { GlobalStateProvider } from "./context/GlobalStateContext";
import { AuthProvider } from "./context/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PrivateRoute from "./components/PrivateRoute";
import Layout from "./layouts/Layout";
import ListLoading from "./components/Shared/ListView/ListLoading";
import "./App.css";

// Lazy Loaded Components
const Dashboard = lazy(() => import("./pages/dashboard"));
const LoginPage = lazy(() => import("./pages/login"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
// Vehicles section disabled
const Customer = lazy(() => import("./pages/customers/Customer"));
const Loan = lazy(() => import("./pages/loan/Loan"));
const SelectCustomer = lazy(() => import("./pages/loan/SelectCustomer"));
const ViewCustomer = lazy(() => import("./pages/customers/ViewCustomer"));
const ViewLoansList = lazy(() => import("./pages/loan/ViewLoansList"));
const ViewLoanDetails = lazy(() => import("./pages/loan/ViewLoanDetails"));
const LoanDetails = lazy(() => import("./pages/loan/CreateLoan"));
const ReceiveLoan = lazy(() => import("./pages/loan/ReceiveLoan"));
const AdminApprovalList = lazy(() => import("./pages/loan/AdminApprovalList"));
const AdminApprovalReview = lazy(() => import("./pages/loan/AdminApprovalReview"));
const LoanHistoryDetails = lazy(() => import("./pages/loan/LoanHistoryDetails"));
const LoanVerification = lazy(() => import("./pages/loan/LoanVerification"));
const LoanUpdate = lazy(() => import("./pages/loan/LoanUpdate"));
const RoleMaster = lazy(() => import("./pages/master/permissions/RoleMaster"));
const UserMaster = lazy(() => import("./pages/master/users/UserMaster"));
const UnapprovedEcs = lazy(() => import("./pages/ecs/UnapprovedEcs"));
const ApproveEcs = lazy(() => import("./pages/ecs/ApproveEcs"));
const Scheme = lazy(() => import("./pages/master/scheme/Scheme"));
const LoanView = lazy(() => import("./pages/loan/LoanView"));
const UpdateScheme = lazy(() => import("./pages/master/scheme/UpdateScheme"));
const UpdateCustomer = lazy(() => import("./pages/customers/UpdateCustomer"));
const CreateCustomer = lazy(() => import("./pages/customers/CreateCustomer"));
const EmiReceiveList = lazy(() => import("./pages/loan/EmiReceiveList"));
const Forclosure = lazy(() => import("./pages/forclosure/Forclosure"));
const ForcloserLoanUpdate = lazy(() => import("./pages/forclosure/ForcloserLoanUpdate"));
const DraftCustomer = lazy(() => import("./pages/customers/DraftCustomer"));

const RejectedLoanView = lazy(() => import("./pages/loan/RejectedLoanView"));
const DraftLoans = lazy(() => import("./pages/loan/DraftLoans"));
const LoanScheduleUpdate = lazy(() => import("./pages/loan/LoanScheduleUpdate"));
const NocPending = lazy(() => import("./pages/loan/closedloan/NocPending"));
const NocIssued = lazy(() => import("./pages/loan/closedloan/NocIssued"));
const EmiReceivingView = lazy(() => import("./pages/emi_receiving/view"));
const EmiReceivingAdd = lazy(() => import("./pages/emi_receiving/add"));
const ENachRegistration = lazy(() => import("./pages/enach/ENachRegistration"));
const TransactionScheduling = lazy(() => import("./pages/enach/TransactionScheduling"));
const TransactionVerification = lazy(() => import("./pages/enach/TransactionVerification"));
const LoanMasterEdit = lazy(() => import("./pages/loan/LoanMasterEdit"));
const PageNotFound = lazy(() => import("./pages/404/page"));
const AccountSettings = lazy(() => import("./pages/profile/page"));
const LoanReport = lazy(() => import("./pages/reports/LoanReport"));
const CollectionReport = lazy(() => import("./pages/reports/CollectionReport"));
const DueReport = lazy(() => import("./pages/reports/DueReport"));

const CustomerPrintPage = lazy(() => import("./pages/customers/CustomerPrintPage"));
const LoanPrintPage = lazy(() => import("./pages/loan/LoanPrintPage"));
const LoanVerifyPrint = lazy(() => import("./pages/loan/LoanVerifyPrint"));
const PaymentPrintPage = lazy(() => import("./pages/loan/PaymentPrintPage"));
const MoneyReceiptPrint = lazy(() => import("./pages/loan/MoneyReceiptPrint"));
const ChangeMandatePage = lazy(() => import("./pages/enach/ChangeMandatePage"));
const MandateChargeReceipt = lazy(() => import("./pages/enach/MandateChargeReceipt"));
const NocPrintPage = lazy(() => import("./pages/loan/NocPrintPage"));

function App() {
  document.addEventListener("ionBackButton", (ev) => {
    ev.detail.register(10, () => {});
  });

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <ToastContainer />
        <GlobalStateProvider>
          <Suspense fallback={<ListLoading message="Loading module..." />}>
            <Routes>
              <Route element={<PrivateRoute />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />

                <Route element={<Layout />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/vehicles" element={<PageNotFound />} />
                  <Route path="/vehicles/:id" element={<PageNotFound />} />
                  <Route path="/interestrate" element={<PageNotFound />} />
                  <Route path="/interestrate/:id" element={<PageNotFound />} />
                  <Route path="/users" element={<UserMaster />} />
                  <Route path="/roles" element={<RoleMaster />} />
                  <Route path="/scheme" element={<Scheme />} />
                  <Route path="/UpdateScheme/:id" element={<UpdateScheme />} />

                  <Route path="/unapprovedecs" element={<UnapprovedEcs />} />
                  <Route path="/approveecs" element={<ApproveEcs />} />

                  <Route path="/customers" element={<Customer />} />
                  <Route path="/draftcustomers" element={<DraftCustomer />} />
                  <Route path="/createcustomers" element={<CreateCustomer />} />
                  <Route path="/createcustomers/:id" element={<CreateCustomer />} />
                  <Route path="/updatecustomer/:id" element={<UpdateCustomer />} />
                  <Route path="/customers/view/:id" element={<ViewCustomer />} />
                  <Route path="/loan" element={<SelectCustomer />} />
                  <Route path="/loan/:id" element={<LoanDetails />} />
                  <Route path="/view-loans" element={<ViewLoansList />} />
                  <Route path="/view-loans/:id" element={<ViewLoanDetails />} />
                  <Route path="/draft-loans" element={<DraftLoans />} />
                  <Route path="/edit-loan/:loanId" element={<LoanDetails />} />
                  <Route path="/admin-approval" element={<AdminApprovalList />} />
                  <Route path="/admin-approval/:id" element={<AdminApprovalReview />} />
                  <Route path="/loan-history/:loanId" element={<LoanHistoryDetails />} />
                  <Route path="/emandate/:id" element={<ENachRegistration />} />
                  <Route path="/change-mandate/:id" element={<ChangeMandatePage />} />
                  <Route path="/mandate-charge-receipt/:chargeId" element={<MandateChargeReceipt />} />
                  <Route path="/schedule-transaction/:id" element={<TransactionScheduling />} />
                  <Route path="/verify-transaction" element={<TransactionVerification />} />
                  <Route path="/loanverification" element={<LoanVerification />} />
                  <Route path="/loanverification/:id" element={<LoanUpdate />} />
                  <Route path="/viewloan" element={<LoanView />} />
                  <Route path="/loan/master-edit/:id" element={<LoanMasterEdit />} />

                  <Route path="/viewloan/:id" element={<LoanScheduleUpdate />} />
                  <Route path="/rejected-loan" element={<RejectedLoanView />} />
                  <Route path="/emi-receive" element={<ReceiveLoan />} />
                  <Route path="/emi-receiving" element={<EmiReceivingView />} />
                  <Route path="/pay-emi/:loanId" element={<EmiReceivingAdd />} />
                  <Route path="/forclosure" element={<Forclosure />} />
                  <Route path="/forclosure/:id" element={<ForcloserLoanUpdate />} />
                  <Route path="/EmiReceiveList/:id" element={<EmiReceiveList />} />
                  <Route path="/noc-pending" element={<NocPending />} />
                  <Route path="/noc-issue" element={<NocIssued />} />
                  <Route path="/reports/loan" element={<LoanReport />} />
                  <Route path="/reports/collection" element={<CollectionReport />} />
                  <Route path="/reports/due" element={<DueReport />} />
                  <Route path="/account-settings" element={<AccountSettings />} />
                </Route>

                <Route path="*" element={<PageNotFound />} />

                {/* ── Print pages: layout-free, auto-print on load ── */}
                <Route path="/customers/print/:id" element={<CustomerPrintPage />} />
                <Route path="/loans/print/:id" element={<LoanPrintPage />} />
                <Route path="/loanverify-print/:id" element={<LoanVerifyPrint />} />
                <Route path="/payment-print/:id" element={<PaymentPrintPage />} />
                <Route path="/receipt-print/:loanId/:emiId" element={<MoneyReceiptPrint />} />
                <Route path="/noc-print/:id" element={<NocPrintPage />} />
              </Route>
            </Routes>
          </Suspense>
        </GlobalStateProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
